/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useCallback, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router-dom';

import { clearWorkspacesListingQueryCache } from '@futrli/views/operations/workspaces/queries/workspacesListingQuery';
import { excelFilesQueryKey } from '@futrli/views/queries/excelFiles';
import { getOnboardingStartUrl } from '@futrli/shared-services/onboarding';
import { Integration } from '@futrli-sdk/app-store';
import { Organisation } from '@futrli-sdk/organisations';
import { OrganisationListItem, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { queryClient } from '@futrli/views/core/queryClient';

import { PREDICT_PRODUCT_SLUG, QUERY_PARAMS, ROUTES } from '../../../app-core';
import { interpolatePathParams, joinRouteMatchUrl } from '../../../path';
import { getProductRoute } from '../../../organisations';
import { UploadEntity } from './upload-entity';
import { useUploadOrganisation } from '../hooks/use-upload-organisation';
import { ExcelUploadDrawer } from './excel-upload-drawer';

export interface OrganisationUploadDrawerInnerProps {
  children: JSX.Element;
  handleClose: () => void;
  maxFileSize?: number; // in MB
  acceptedFileExtensions?: string;
  workspaceId: string;
  excelIntegration: Integration | undefined;
  isUpdateDrawer?: boolean;
  isOnboarding?: boolean;
  workspaceByBusinessId?: WorkspacesListingItem;
  organisationByBusinessId?: OrganisationListItem;
  businessId?: string | null;
  organisation?: Organisation;
  disableCreate?: boolean;
  countryCode?: string;
}

export const OrganisationUploadDrawerInner = ({
  children,
  handleClose,
  workspaceId,
  maxFileSize = 25,
  acceptedFileExtensions = '.xls,.xlsx',
  excelIntegration,
  isUpdateDrawer = false,
  isOnboarding = false,
  workspaceByBusinessId,
  organisationByBusinessId,
  businessId,
  organisation,
  disableCreate,
  countryCode
}: OrganisationUploadDrawerInnerProps) => {
  const flags = useFlags();
  const navigate = useNavigate();
  const [file, setFile] = useState<File>();
  const {
    createOrganisationFromExcelFile,
    createSfaOrganisationFromExcelFile,
    updateOrganisationFromExcelFile,
    uploadFailed,
    setUploadFailed,
    uploadSuccess,
    setUploadSuccess,
    isInProgress,
    step,
    setStep,
    errorMessage,
    setErrorMessage,
    createdOrgId
  } = useUploadOrganisation(workspaceId);

  useEffect(() => {
    if (uploadSuccess && organisation?.id) {
      queryClient.invalidateQueries({
        queryKey: excelFilesQueryKey(workspaceId, organisation.id, {})
      });
      clearWorkspacesListingQueryCache();
    }
  }, [uploadSuccess, workspaceId, organisation?.id]);

  const onFileChange = useCallback(
    (file: File) => {
      setFile(file);
      setErrorMessage('');
      setUploadFailed(false);
    },
    [setErrorMessage, setUploadFailed]
  );

  useEffect(() => {
    if (uploadFailed) {
      setFile(undefined);
      setStep('ready');
    }
  }, [uploadFailed, setStep]);

  useEffect(() => {
    if (uploadSuccess) {
      setFile(undefined);
      setStep('ready');
    }
  }, [uploadSuccess, setStep]);

  const onCancel = async () => {
    if (uploadSuccess && createdOrgId) {
      const productUrl = interpolatePathParams(getProductRoute(), {
        workspaceId,
        organisationId: createdOrgId,
        productSlug: PREDICT_PRODUCT_SLUG
      });
      if (isOnboarding) {
        navigate(
          `${getOnboardingStartUrl()}?${QUERY_PARAMS.WORKSPACE_ID}=${workspaceId}&${
            QUERY_PARAMS.ORG_ID
          }=${createdOrgId}&${QUERY_PARAMS.STATUS}=SUCCESS&${QUERY_PARAMS.INTEGRATION}=excel`
        );
      } else {
        navigate(joinRouteMatchUrl('/', productUrl, ROUTES.HOME));
      }
    } else {
      setFile(undefined);
      setErrorMessage('');
      setStep('ready');
      setUploadSuccess(false);
      handleClose();
    }
  };

  const onSubmit = useCallback(() => {
    if (file && !isInProgress && step === 'ready') {
      if (workspaceByBusinessId && organisationByBusinessId && businessId) {
        createSfaOrganisationFromExcelFile(
          file,
          businessId,
          workspaceByBusinessId,
          organisationByBusinessId,
          excelIntegration,
          countryCode
        );
      } else if (!isUpdateDrawer) {
        createOrganisationFromExcelFile(file, excelIntegration, countryCode);
      } else if (organisation) {
        updateOrganisationFromExcelFile(file, excelIntegration, organisation);
      }
    }
  }, [
    businessId,
    workspaceByBusinessId,
    organisationByBusinessId,
    countryCode,
    createOrganisationFromExcelFile,
    createSfaOrganisationFromExcelFile,
    excelIntegration,
    file,
    isInProgress,
    isUpdateDrawer,
    organisation,
    step,
    updateOrganisationFromExcelFile
  ]);

  // If this is an update drawer / excel org country code flag isnt enabled, run onSubmit as soon as file changes
  useEffect(() => {
    if (!flags.excelOrgCountryCode || isUpdateDrawer) {
      onSubmit();
    }
  }, [flags.excelOrgCountryCode, isUpdateDrawer, onSubmit]);

  return (
    <ExcelUploadDrawer
      maxFileSize={maxFileSize}
      acceptedFileExtensions={acceptedFileExtensions}
      onCancel={onCancel}
      onFinish={onCancel}
      onCreate={flags.excelOrgCountryCode && !isUpdateDrawer ? onSubmit : undefined}
      isUpdateDrawer={isUpdateDrawer}
      workspaceId={workspaceId}
      organisationId={organisation?.id}
      historyConfig={{ entityType: 'organisation' }}
      isInProgress={isInProgress}
      uploadSuccess={uploadSuccess}
      uploadFailed={uploadFailed}
      onFileChange={onFileChange}
      file={file}
      errorMessage={errorMessage}
      step={step}
      entity={UploadEntity.Organisation}
      disableCreate={!file || !!disableCreate}
    >
      {children}
    </ExcelUploadDrawer>
  );
};
